<template>
  <div class="rental-layout">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Rental (ID: {{ $route.params.id }})</h1>
          <div class="manager-title" v-if="statusCode !== 'draft'">
            <app-switcher
              @change="onChangeResupplyFee(rental_detail.resupply_fee)"
              v-model="rental_detail.resupply_fee"
              :readonly="statusCode === 'completed'"
            />
            <span class="manager-title__name">Resupply fee</span>
          </div>
          <div class="status-title" :style="{ 'backgroundColor': rental_detail.status.color }">
            {{ rental_detail.status.name }}
          </div>
          <div
            v-if="statusCode !== 'draft'"
            class="status-tag"
            :style="{ 'backgroundColor': rental_detail.balance === 0 ? '#77b017' : rental_detail.balance > 0 ? '#f0c427' :'#e85c20' }"
          >
            {{ rental_detail.balance === 0 ? 'Paid' : `Balance: ${rental_detail.balance}$` }}
          </div>
          <div v-if="rental_detail.responsible_manager" class="manager-title">
            <div class="manager-title__photo">
              <img :src="getUserAvatar(rental_detail.responsible_manager.crop.crop_url)" alt="Avatar">
            </div>
            <span class="manager-title__name">{{ rental_detail.responsible_manager.full_name }}</span>
          </div>
        </app-cells>
        <app-button v-if="rental_detail.status.code === 'draft'" theme="error" @click.native="onShowModalDelete">
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Group:</span>
              <router-link :to="{ name: 'climbing-groups-detail', params: { id: rental_detail.client.group.id } }" class="info-cards__link">
                {{ rental_detail.client.group.name }}
              </router-link>
              <span>({{ rental_detail.client.group.route.name }} {{ rental_detail.client.group.route.duration }})</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Tour start:</span><span>{{ dateFormat(rental_detail.client.group.tour_start) }}</span>
            </div>
            <div class="info-cards__row-item">
              <span>Tour finish:</span><span>{{ dateFormat(rental_detail.client.group.tour_end) }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Client:</span>
              <router-link :to="{ name: 'clients-detail', params: { id: rental_detail.client.client.id } }" class="info-cards__link">
                {{ rental_detail.client.client.full_name }}
              </router-link>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Rent start:</span><span>{{ dateFormat(rental_detail.rental_start) }}</span>
            </div>
            <div class="info-cards__row-item">
              <span>Rent finish:</span><span>{{ dateFormat(rental_detail.rental_end) }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <button v-if="rental_detail.status.code !== 'completed'" class="info-cards__control" @click="onShowDiscountModal">
            <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
          </button>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Rental:</span><span>{{ rental_detail.amount_rental }}$</span>
            </div>
            <div class="info-cards__row-item">
              <span>Discount:</span>
              <span>{{ rental_detail.discount_amount || 0 }}$</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Purchase:</span><span>{{ rental_detail.amount_sale }}$</span>
            </div>
            <div class="info-cards__row-item">
              <span>Total:</span><span>{{ rental_detail.amount }}$</span>
            </div>
          </div>
        </div>
      </div>
      <app-tabs :tabs="tabs" />
      <transition name="fade" mode="out-in">
        <router-view :status="statusCode" @getRentalInfo="getRentalInfo" />
      </transition>
      <app-cells position="end" class="cells--end-vertical">
        <app-button
          v-if="rental_detail.status && rental_detail.status.code === 'in_process'"
          @click.native="onPrint"
          type="button"
          theme="warn"
          ref="print"
        >
          Print rental
        </app-button>
        <app-button
          v-if="rental_detail.status && rental_detail.status.code !== 'completed'"
          @click.native="$modal.show('accept-modal')"
        >
          {{ rental_detail.status.code === 'draft' ? 'Start rental' : rental_detail.status.code === 'in_process' ? 'Complete rental' : '' }}
        </app-button>
      </app-cells>
    </template>
    <modal
      name="accept-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('accept-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Change status
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to change status?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('accept-modal')" theme="dark">Cancel</app-button>
            <app-button @click.native="onChangeMainStatus" ref="main_status">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete--rental-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideDeleteModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete rental
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideDeleteModal" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteRental" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="discount-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('discount-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Discount</div>
        <form @submit.prevent="onCheckDiscountForm">
          <app-form-group label="Discount amount" label-for="discount-amount" required>
            <app-input
              v-model.number="discount_form.discount_amount"
              type="number"
              min="0"
              id="discount-amount"
              name="discount-amount"
              placeholder="Discount amount"
              :error="$v.discount_form.discount_amount.$error"
            />
            <template #error>
              <div v-if="$v.discount_form.discount_amount.$dirty && !$v.discount_form.discount_amount.required">Required field</div>
            </template>
          </app-form-group>
          <app-form-group
            v-if="discount_form.discount_amount > 0"
            label="Discount description"
            label-for="discount_description"
            required
          >
            <app-textarea
              v-model="discount_form.discount_description"
              rows="5"
              placeholder="Discount description"
              id="discount_description"
              :error="$v.discount_form.discount_description.$error"
            />
            <template #error>
              <div v-if="$v.discount_form.discount_description.$dirty && !$v.discount_form.discount_description.required">Required field</div>
            </template>
          </app-form-group>
          <app-cells position="center" :indent="false">
            <app-button type="submit" ref="discountSubmit" :disabled="$v.discount_form.$error">Save discount</app-button>
          </app-cells>
        </form>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getRental,
  patchRental,
  deleteRental,
  getRentalPrint,
} from '@/http'
import { format, parse } from 'date-fns'
import AppTabs from '@/components/AppTabs'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'RentalDetailLayout',
  components: { AppTabs },
  data() {
    return {
      rental_detail: {},
      is_loaded: false,
      tabs: [
        { name: 'rental-items', label: 'Rental' },
        { name: 'rental-purchase', label: 'Purchase' },
        { name: 'rental-payments', label: 'Payments' },
      ],
      discount_form: {},
    }
  },
  validations: {
    discount_form: {
      discount_amount: { required },
      discount_description: {
        required: requiredIf(function() {
          return this.discount_form.discount_amount > 0
        })
      },
    },
  },
  created() {
    document.title = `Rental (ID: ${ this.$route.params.id }) | Altezza Board`
    this.getRentalInfo()
  },
  computed: {
    statusCode() {
      return this.rental_detail.status.code
    },
  },
  methods: {
    onChangeResupplyFee(data) {
      patchRental(this.$route.params.id, { resupply_fee: data })
        .then(() => {
          this.getRentalInfo()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
        })
    },
    onShowDiscountModal() {
      this.$modal.show('discount-modal')
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    onPrint() {
      this.$refs.print.preload = true
      getRentalPrint(this.$route.params.id)
        .finally(() => { this.$refs.print.preload = false })
        .then(response => { window.open(response.data) })
    },
    tooltipContent(data) {
      if (!data.length && !this.rental_detail.paid) return ''
      let finalString = ''
      data.forEach(item => {
        finalString += `<span>${item.method.name} - ${item.amount}$ - ${item.responsible.first_name} ${item.responsible.last_name}${item.bank ? ` (${item.bank.name})` : '' }</span>`
      })
      return finalString
    },
    onCheckDiscountForm() {
      this.$v.discount_form.$touch()
      if (this.$v.discount_form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendDiscountForm()
      }
    },
    sendDiscountForm() {
      this.$refs.discountSubmit.preload = true
      if (this.discount_form.discount_amount <= 0) this.discount_form.discount_description = null
      patchRental(this.$route.params.id, this.discount_form)
        .then(() => {
          this.getRentalInfo()
          this.$notify({
            type: 'success',
            title: 'Discount saved',
          })
          this.$modal.hide('discount-modal')
        })
        .catch(() => { this.$refs.discountSubmit.preload = false })
    },
    onShowModalDelete() {
      this.$modal.show('delete--rental-modal')
    },
    onHideDeleteModal() {
      this.$modal.hide('delete--rental-modal')
    },
    onDeleteRental() {
      this.$refs.delete.preload = true
      deleteRental(this.$route.params.id)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Rental deleted',
          })
          this.$router.push({name: 'rentals-active'})
        })
    },
    getRentalInfo() {
      getRental(this.$route.params.id).then(res => {
        this.rental_detail = res.data
        this.discount_form = {
          discount_amount: res.data.discount_amount || 0,
          discount_description: res.data.discount_description
        }
        this.is_loaded = true
      })
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    onChangeMainStatus() {
      this.$refs.main_status.preload = true
      const data = {
        status: this.rental_detail.status.code === 'draft' ? 'in_process' : 'completed'
      }
      patchRental(this.$route.params.id, data)
        .finally(() => { this.$refs.main_status.preload = false })
        .then(() => {
          this.getRentalInfo()
          this.$notify({
            type: 'success',
            title: 'Rental status changed',
          })
          this.$modal.hide('accept-modal')
        })
    },
  }
}
</script>
<style lang="sass" scoped>
.rental-layout
  position: relative
  min-height: 200px
</style>
